// API contract types — field names mirror the FastAPI responses exactly
// (snake_case) so there's no lossy mapping layer between server and UI.

export type Role = 'system_admin' | 'org_admin' | 'member';

export interface Organization {
  id: string;
  name: string;
  created_at: string;
}
export type SessionStatus = 'pending' | 'active' | 'completed' | 'error';
export type TestMode = 'ap_client' | 'mesh';
export type Protocol = 'tcp' | 'udp';
export type Direction = 'down' | 'up' | 'both';
export type StreamRole = 'transmitter' | 'receiver';

export interface User {
  id: string;
  email: string;
  display_name: string | null;
  role: Role;
  org_id: string | null;
  disabled: boolean;
  can_delete_sessions: boolean;
  can_delete_users: boolean;
  traffic_seconds_balance: number;
  created_at: string;
  last_login_at: string | null;
}

// Role helpers — a single place so gating doesn't scatter role literals.
export const isSystemAdmin = (u?: { role: Role } | null): boolean => u?.role === 'system_admin';
export const isAdmin = (u?: { role: Role } | null): boolean =>
  u?.role === 'system_admin' || u?.role === 'org_admin';

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface Agent {
  id: string;
  external_id: string;
  label: string | null;
  platform: string | null;
  capabilities: string[];
  psk_kid: string;
  agent_version: string | null;
  last_seen_at: string | null;
  last_ip: string | null;
  owner_user_id?: string | null;
  org_id?: string | null;
  created_at: string;
}

export interface StreamOut {
  id: string;
  receiver_agent_id: string | null;
  protocol: Protocol;
  direction: Direction;
  bitrate_mbps: number | null;
  packet_size: number | null;
  tos: number | null; // IP TOS byte (DSCP << 2)
  traffic_type?: string | null; // emulated application, e.g. 'video-stream'
  status: string;
}

export interface Session {
  id: string;
  label: string | null;
  test_mode: TestMode;
  status: SessionStatus;
  transmitter_agent_id: string | null;
  created_by: string | null;
  created_by_email?: string | null; // owner — the account that ran the test
  created_by_name?: string | null;
  org_id?: string | null;
  created_via: string | null;
  started_at: string | null;
  stopped_at: string | null;
  error_reason: string | null;
  source_ip: string | null;
  duration_seconds: number;
  planned_duration_seconds?: number; // requested run length; 0 = unlimited
  summary_json: SessionSummary | null; // materialised at /stop
  created_at: string;
  streams?: StreamOut[] | null; // populated on the detail (GET) endpoint
  firmware?: string | null;
  tag?: string | null;
  thresholds?: Thresholds | null;
  expectations?: Expectations | null;
  verdict?: 'pass' | 'fail' | null; // null = no thresholds set
  verdict_json?: Verdict | null;
}

export interface Thresholds {
  min_throughput_mbps?: number | null;
  max_latency_ms?: number | null;
  max_loss_pct?: number | null;
  max_jitter_ms?: number | null;
  max_retransmits?: number | null;
}

export interface VerdictCheck {
  metric: string;
  op: string; // '>=' | '<='
  threshold: number;
  actual: number | null;
  ok: boolean;
}

// What the operator expected the link to negotiate. Not thresholds — these
// produce findings ("advertised Wi-Fi 7, negotiated 11ax"), not a pass/fail SLA.
export interface Expectations {
  wifi_standard?: string;
  wifi_security?: string;
  wifi_band?: string;
}

export interface Verdict {
  result: 'pass' | 'fail';
  checks: VerdictCheck[];
}

// POST /api/sessions/{id}/start  — one entry per stream to run.
export interface StreamSpec {
  receiver_agent_id: string;
  protocol: Protocol;
  direction: Direction;
  bitrate_mbps: number; // 0 = unlimited
  packet_size?: number;
  tos?: number;
  // Which application this stream emulates (traffic-profile id, or 'custom').
  // Records intent, which `tos` alone doesn't — gaming and 4K share a DSCP —
  // and is what the analysis engine keys off to compare traffic classes.
  traffic_type?: string;
}

export interface StartSessionRequest {
  transmitter_agent_id: string;
  streams: StreamSpec[];
  duration_seconds?: number; // 0 / omitted = unlimited (run until stopped)
}

export interface CreateSessionRequest {
  label?: string | null;
  test_mode: TestMode;
  firmware?: string | null;
  tag?: string | null;
  thresholds?: Thresholds | null;
  expectations?: Expectations | null;
}

// ---- /api/keys (robot API keys) ----
export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  created_at: string;
  last_used_at?: string | null;
  revoked_at?: string | null;
}
export interface ApiKeyCreated extends ApiKey {
  token: string; // shown ONCE
}

// ---- /api/templates ----
export interface TemplateConfig {
  label?: string | null;
  test_mode: TestMode;
  firmware?: string | null;
  tag?: string | null;
  thresholds?: Thresholds | null;
  expectations?: Expectations | null;
  transmitter_agent_id: string;
  streams: StreamSpec[];
}
export interface Template {
  id: string;
  name: string;
  config: TemplateConfig;
  created_at: string;
  updated_at: string;
}

// ---- /api/schedules ----
export type ScheduleKind = 'once' | 'cron';
export interface Schedule {
  id: string;
  name: string;
  template_id: string;
  kind: ScheduleKind;
  run_at?: string | null;
  cron?: string | null;
  timezone: string;
  enabled: boolean;
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_session_id?: string | null;
  last_status?: string | null;
  created_at: string;
  updated_at: string;
}
export interface ScheduleCreate {
  name: string;
  template_id: string;
  kind: ScheduleKind;
  run_at?: string | null;
  cron?: string | null;
  timezone?: string;
  enabled?: boolean;
}

// ---- GET /api/sessions/{id}/metrics ----
export interface MetricPoint {
  stream_id: string;
  role: StreamRole;
  ts: string;
  throughput_mbps: number | null;
  latency_ms: number | null;
  jitter_ms: number | null;
  loss_pct: number | null;
  retransmits: number | null;
  loss_events?: LossEventPoint[] | null;
  rx_throughput_mbps?: number | null; // download (what the receiver received)
  tx_throughput_mbps?: number | null; // upload (what the receiver sent)
}

// One BSSID change (WiFi steering/roam event) during a session.
export interface Roam {
  ts: string;
  from_bssid: string | null;
  to_bssid: string;
}

export interface LossEventPoint {
  t_ms: number; // epoch ms the gap was detected (server-corrected)
  n: number; // packets in this gap
}

export interface SessionSummary {
  tx_avg_mbps: number | null;
  tx_max_mbps: number | null;
  rx_avg_mbps: number | null;
  rx_max_mbps: number | null;
  download_avg_mbps?: number | null;
  download_max_mbps?: number | null;
  upload_avg_mbps?: number | null;
  upload_max_mbps?: number | null;
  latency_avg_ms: number | null;
  latency_max_ms: number | null;
  latency_idle_ms?: number | null; // near-unloaded floor (bufferbloat baseline)
  latency_loaded_ms?: number | null; // loaded tail (p95)
  bufferbloat_ms?: number | null; // loaded − idle
  bufferbloat_grade?: string | null; // A–F
  latency_p50_ms?: number | null;
  latency_p95_ms?: number | null;
  latency_p99_ms?: number | null;
  latency_up_avg_ms?: number | null; // uplink latency (transmitter-measured; both tests)
  latency_up_max_ms?: number | null;
  latency_up_p95_ms?: number | null;
  jitter_up_avg_ms?: number | null;
  loss_up_avg_pct?: number | null;
  loss_up_max_pct?: number | null;
  jitter_avg_ms: number | null;
  jitter_max_ms: number | null;
  loss_avg_pct: number | null;
  loss_max_pct: number | null;
  qoe_mos?: number | null;      // E-model MOS 1.0–4.5
  qoe_rating?: string | null;   // Excellent…Bad
  zero_reason?: string | null; // plain-language "why 0 throughput"
  bytes_sent: number;
  bytes_received: number;
  packets_sent: number;
  packets_received: number;
  retransmits_total: number;
  sample_count: number;
  stream_count: number;
  wifi_rssi_avg: number | null;
  wifi_rssi_min: number | null;
  wifi_band: string | null;
  wifi_channel: number | null;
  wifi_bssid?: string | null;
  wifi_samples: number;
  // Richer link/device context (mobile agents, mostly Android).
  wifi_standard?: string | null;
  wifi_security?: string | null;
  tx_phy_max_mbps?: number | null;
  rx_phy_max_mbps?: number | null;
  thermal_worst?: string | null;
  battery_min_pct?: number | null;
  cochannel_max?: number | null;
  on_cellular_any?: boolean | null;
  power_save_any?: boolean | null;
  roams?: Roam[];
  roam_count?: number;
  // Automatic interpretation of the run. Distinct from `verdict`, which is the
  // operator's own pass/fail SLA — these are conclusions the platform reached
  // unprompted, each with the evidence behind it.
  findings?: Finding[];
  per_stream?: StreamStats[];
}

export type FindingSeverity = 'good' | 'info' | 'warn' | 'bad';

export interface Finding {
  code: string;              // stable id, e.g. 'qos.working'
  severity: FindingSeverity;
  title: string;             // one-line conclusion
  detail: string;            // the reasoning, in plain language
  evidence: Record<string, unknown>;
  confidence: 'high' | 'medium';
  // Where this finding points: which summary readouts, which traffic-class
  // rows, and which moments on the chart it is talking about. The UI stamps the
  // finding's number on each so the reader never has to hunt for the figure.
  summary_fields?: string[];
  stream_ids?: string[];
  marks?: { ts: string; label?: string }[];
}

export interface StreamStats {
  stream_id: string;
  traffic_type?: string | null;
  tos?: number | null;
  dscp: number;
  protocol: Protocol;
  direction: Direction;
  requested_mbps?: number | null;
  throughput_avg_mbps?: number | null;
  throughput_max_mbps?: number | null;
  latency_avg_ms?: number | null;
  latency_max_ms?: number | null;
  latency_idle_ms?: number | null;
  latency_p50_ms?: number | null;
  latency_p95_ms?: number | null;
  latency_p99_ms?: number | null;
  jitter_avg_ms?: number | null;
  loss_avg_pct?: number | null;
  loss_max_pct?: number | null;
  sample_count: number;
  throughput_cv?: number | null;
}

export interface SessionMetricsResponse {
  summary: SessionSummary;
  points: MetricPoint[];
  points_truncated: boolean;
}

// ---- /ws/live/{session_id} frames ----
export interface SubscribedEvent {
  event: 'subscribed';
  session_id: string;
}

export interface MetricFrame {
  event: 'metric_frame';
  session_id: string;
  stream_id: string;
  role: StreamRole;
  ts: string;
  throughput_mbps: number | null;
  rx_throughput_mbps?: number | null;
  tx_throughput_mbps?: number | null;
  latency_ms: number | null;
  latency_p95_ms?: number | null;
  latency_p99_ms?: number | null;
  jitter_ms: number | null;
  loss_pct: number | null;
  retransmits: number | null;
  loss_events?: LossEventPoint[] | null;
}

export interface ShutdownEvent {
  event: 'server.shutdown';
}

export type LiveEvent = SubscribedEvent | MetricFrame | ShutdownEvent;

// ---- /api/admin/overview (system-admin BI) ----
export interface KV { label: string; value: number; }
export interface DayPoint { day: string; sessions: number; credits: number; }
export interface TopOrg { org_id: string | null; name: string; credits: number; sessions: number; }
export interface TopUser { user_id: string; email: string; org: string | null; credits: number; sessions: number; }
export interface RecentError { id: string; reason: string | null; org: string | null; at: string | null; }
export interface AdminOverview {
  users: number; orgs: number; agents: number; agents_online: number;
  sessions_total: number; sessions_completed: number; sessions_error: number; sessions_active: number;
  credits_consumed: number; credits_outstanding: number; credits_last_24h: number;
  error_rate: number; avg_duration_s: number;
  timeseries: DayPoint[];
  by_status: KV[]; by_error: KV[]; by_test_mode: KV[];
  top_orgs: TopOrg[]; top_users: TopUser[]; recent_errors: RecentError[];
  generated_at: string;
}
